import { memo, useMemo } from "react";

import { Column, FormatType, TimeseriesChart, TooltipText, useAppPalette, SERIES_FORMATTERS } from "@bridgesplit/ui";
import { EMPTY_STRING } from "@bridgesplit/utils";
import { formatDurationWithTypeShorthand, getDurationIndex } from "@bridgesplit/abf-sdk";

import { useMarketContext } from "./MarketContext";

type YieldCurvePoint = {
    date: number; // duration index — required by TimeseriesChart's `{ date: number }` shape
    rate: number;
    durationLabel: string;
    principalUsd: number;
};

export default memo(function YieldCurve({
    height = 270,
    includeHeader = true
}: {
    height?: number;
    includeHeader?: boolean;
}) {
    const { principalStats } = useMarketContext();
    const { secondary } = useAppPalette();
    const buckets = principalStats?.principalStats.durationToWAvgBorrowApy;
    const principalPrice = principalStats?.principalStats.principalPrice ?? 0;

    const chartData = useMemo<YieldCurvePoint[]>(() => {
        if (!buckets) return [];
        return buckets
            .map((b) => {
                const idx = getDurationIndex({ duration: b.duration, durationType: b.durationType });
                if (idx === undefined) return undefined;
                return {
                    date: idx,
                    rate: b.wAvgApy,
                    durationLabel: formatDurationWithTypeShorthand({
                        duration: b.duration,
                        durationType: b.durationType
                    }),
                    principalUsd: b.principalDue * principalPrice
                };
            })
            .filter((p): p is YieldCurvePoint => p !== undefined)
            .sort((a, b) => a.date - b.date);
    }, [buckets, principalPrice]);

    const getLabel = useMemo(() => new Map(chartData.map((d) => [d.date, d.durationLabel])), [chartData]);

    const chart = (
        <TimeseriesChart<YieldCurvePoint>
            chartId="yield-curve"
            data={chartData}
            height={height}
            axisFormat={FormatType.Percent}
            series={[
                {
                    dataKey: "rate",
                    label: "Avg. APY",
                    color: secondary
                },
                {
                    dataKey: "principalUsd",
                    label: "Borrowed",
                    valueFormatter: SERIES_FORMATTERS.Usd,
                    hideLine: true
                }
            ]}
            xAxis={{
                scaleOverride: "band",
                groups: [{ getValue: (date: number) => getLabel.get(date) ?? EMPTY_STRING }],
                valueFormatter: (date: number) => getLabel.get(date) ?? EMPTY_STRING
            }}
            hideLegend
        />
    );

    if (!includeHeader) return chart;
    return (
        <Column width="100%" spacing={2}>
            <TooltipText helpText="Weighted-avg borrow APY by duration across active loans" variant="h4">
                Yield curve
            </TooltipText>
            {chart}
        </Column>
    );
});
